import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import EventContactFormDialog from "@/components/dialogs/EventContactFormDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Trash2, Users2 } from "lucide-react";

export default function EventContacts() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const load = (q) => api.get("/event-contacts", { params: q ? { search: q } : {} }).then((r) => setContacts(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    load(e.target.value);
  };

  const deleteContact = async (id) => {
    try {
      await api.delete(`/event-contacts/${id}`);
      toast.success("Contact removed");
      load(search);
    } catch (e) {
      toast.error("Could not remove contact");
    }
  };

  return (
    <DashboardLayout title="Event Contacts">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="relative w-80">
          <Search className="h-4 w-4 text-[#4B5563] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="event-contact-search-input"
            placeholder="Search name, company, email, mobile..."
            value={search}
            onChange={handleSearch}
            className="pl-9"
          />
        </div>
        <Button data-testid="add-event-contact-button" onClick={() => setAddOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg py-16 text-center text-[#4B5563]" data-testid="event-contacts-empty-state">
          <Users2 className="h-8 w-8 mx-auto mb-2 text-[#76C121]" />
          No contacts logged yet. Add people you meet at events and conferences here.
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="event-contacts-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Mobile</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Company</th>
                <th className="px-5 py-3 font-medium">Met At</th>
                <th className="px-5 py-3 font-medium">Comments</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} data-testid={`event-contact-row-${c.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <td className="px-5 py-3 font-medium text-[#1C1C1E]">{c.name}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{c.mobile || "—"}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{c.company_name || "—"}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{c.met_at || "—"}</td>
                  <td className="px-5 py-3 text-[#4B5563] max-w-[220px] truncate">{c.comments || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => deleteContact(c.id)} data-testid={`event-contact-delete-${c.id}`} className="text-[#4B5563] hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EventContactFormDialog open={addOpen} onOpenChange={setAddOpen} onCreated={() => load(search)} />
    </DashboardLayout>
  );
}
